'use client'

/**
 * Purpose: Survey page 1 — name, email (when not prefilled), age, and gender.
 * Inputs: formState, setFormState, optional prefillProfile from onboarding.
 * Outputs: Controlled inputs and RadioGroups that update parent form state.
 */

import type { Dispatch, SetStateAction } from 'react'
import { RadioGroup } from '@components/forms/RadioGroup/RadioGroup'
import type { FormState } from '@lib/models/types'
import type { SurveyPrefillProfile } from '@components/forms/SurveyForm/hooks/use-survey-form'

const AGE_CHOICES = ['under18', '18-24', '25-34', '35-44', '45-54', '55-64', '65over'] as const
const GENDER_CHOICES = ['male', 'female', 'other'] as const

// Demographics collected before axis questions — email skipped when prefilled.
export function SurveyClientInfoPage({
  formState,
  setFormState,
  prefillProfile,
  title,
  tPages,
}: {
  formState: FormState
  setFormState: Dispatch<SetStateAction<FormState>>
  prefillProfile?: SurveyPrefillProfile
  title: string
  tPages: (key: string) => string
}) {
  // tPages scopes copy to survey.pages.* translation keys.
  return (
    <div id="survey-client-info-page">
      <h1 id="survey-client-info-page-title" className="mb-6">
        {title}
      </h1>
      {/* Onboarding users are already identified — skip name/email. */}
      {!prefillProfile && (
        <>
          {/* Logged-out visitors must supply contact info; onboarding skips these fields. */}
          <div id="survey-client-info-name-field" className="mb-4">
            <label htmlFor="survey-name" className="block question-bold mb-1">
              {tPages('clientInfo.name')} *
            </label>
            <input
              id="survey-name"
              type="text"
              value={formState.name}
              onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
              className="w-full bg-transparent border border-orange rounded-full px-4 py-2 text-black placeholder-black/40 focus:outline-none focus:ring-1 focus:ring-orange"
              placeholder={tPages('clientInfo.namePlaceholder')}
            />
          </div>
          {/* Email is required for logged-out survey completion and follow-up. */}
          <div id="survey-client-info-email-field" className="mb-4">
            <label htmlFor="survey-email" className="block question-bold mb-1">
              {tPages('clientInfo.email')} *
            </label>
            <input
              id="survey-email"
              type="email"
              value={formState.email}
              onChange={(e) => setFormState((s) => ({ ...s, email: e.target.value }))}
              className="w-full bg-transparent border border-orange rounded-full px-4 py-2 text-black placeholder-black/40 focus:outline-none focus:ring-1 focus:ring-orange"
              placeholder={tPages('clientInfo.emailPlaceholder')}
            />
          </div>
        </>
      )}
      {/* Optional demographics — used for aggregate analytics, not scoring. */}
      <RadioGroup
        name="age"
        label={tPages('clientInfo.age')}
        choices={AGE_CHOICES.map((k) => ({
          key: k,
          label: tPages(`clientInfo.ageChoices.${k}`),
        }))}
        value={formState.age}
        onChange={(age) => setFormState((s) => ({ ...s, age }))}
      />
      {/* Gender is optional demographics for aggregate analytics. */}
      <RadioGroup
        name="gender"
        label={tPages('clientInfo.gender')}
        choices={GENDER_CHOICES.map((k) => ({
          key: k,
          label: tPages(`clientInfo.genderChoices.${k}`),
        }))}
        value={formState.gender}
        onChange={(gender) => setFormState((s) => ({ ...s, gender }))}
      />
    </div>
  )
}
